import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  PRICING_PLANS,
  TRIAL_DAYS,
  SERVER_LIMITS_BY_TIER,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ms: number) {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function tierLabel(tier: string) {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

function getPlanPrice(tier: string): number | null {
  const plan = PRICING_PLANS.find((p) => p.id === tier);
  return plan?.priceMonthly ?? null;
}

function getPlanFeatures(tier: string): readonly string[] {
  const plan = PRICING_PLANS.find((p) => p.id === tier);
  return plan?.features ?? [];
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  cancelAtPeriodEnd,
}: {
  status: SubscriptionStatus;
  cancelAtPeriodEnd: boolean;
}) {
  if (cancelAtPeriodEnd) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Cancels at period end
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
        Active
      </span>
    );
  }
  if (status === "trialing") {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        Free Trial
      </span>
    );
  }
  if (status === "past_due") {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Past Due
      </span>
    );
  }
  if (status === "canceled") {
    return (
      <span className="inline-flex items-center rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-semibold text-muted">
        Canceled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-semibold text-muted">
      Free plan
    </span>
  );
}

// ─── Usage bar ────────────────────────────────────────────────────────────────

function UsageBar({ used, limit }: { used: number; limit: number }) {
  if (limit === 0) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const isNearLimit = pct >= 80;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs text-muted">
        <span>{used} of {limit} used</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-foreground/10">
        <div
          className={`h-full rounded-full transition-all ${
            isNearLimit ? "bg-amber-500" : "bg-primary"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Check icon ───────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg
      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardBillingPage() {
  usePageTitle("Billing");

  const summary = useQuery(api.billing.getMySummary);
  const openPortal = useAction(api.actions.openCustomerPortal);

  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");

  // ── Loading ──
  if (summary === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-pulse text-muted">Loading billing...</div>
      </div>
    );
  }

  // ── Derived state ──
  const tier = (summary?.tier ?? "free") as SubscriptionTier;
  const status = (summary?.status ?? "none") as SubscriptionStatus;
  const hasSubscription = !!(summary?.subscriptionId);
  const isTrialing = status === "trialing";
  const cancelAtPeriodEnd = summary?.cancelAtPeriodEnd ?? false;
  const currentPeriodEnd = summary?.currentPeriodEnd ?? null;
  const trialEndsAt = summary?.trialEndsAt ?? null;
  const activeOrgs = summary?.usage?.activeOrgs ?? 0;
  const serverLimit = SERVER_LIMITS_BY_TIER[tier] ?? 0;
  const planPrice = getPlanPrice(tier);
  const planFeatures = getPlanFeatures(tier);

  // Next billing date label
  let billingDateLabel = "—";
  let billingDateNote = "";
  if (isTrialing && trialEndsAt) {
    billingDateLabel = formatDate(trialEndsAt);
    billingDateNote = `Your ${TRIAL_DAYS}-day free trial ends on this date.`;
  } else if (cancelAtPeriodEnd && currentPeriodEnd) {
    billingDateLabel = formatDate(currentPeriodEnd);
    billingDateNote = "Access ends on this date. No further charges.";
  } else if (currentPeriodEnd) {
    billingDateLabel = formatDate(currentPeriodEnd);
    billingDateNote = "Auto-renews on this date.";
  }

  // Trial days remaining
  const trialDaysLeft =
    isTrialing && trialEndsAt
      ? Math.max(0, Math.ceil((trialEndsAt - Date.now()) / (1000 * 60 * 60 * 24)))
      : null;

  // ── Handlers ──
  const handleOpenPortal = async () => {
    setPortalError("");
    setPortalLoading(true);
    try {
      const result = await openPortal({});
      if (!result.ok) throw new Error(result.error?.message || "Portal unavailable");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setPortalError(err instanceof Error ? err.message : "Unable to open customer portal.");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-sm text-muted">View your plan, usage, and manage payment details</p>
        </div>
        <Link to="/dashboard/settings" className="text-sm text-primary hover:underline">
          Back to Settings
        </Link>
      </div>

      {/* Trial banner */}
      {isTrialing && trialEndsAt && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              Free Trial
            </span>
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} remaining
            </span>
          </div>
          <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            Your trial ends on <span className="font-medium">{formatDate(trialEndsAt)}</span>.
            Your card on file will be charged automatically if you don't cancel before then.
          </p>
        </div>
      )}

      {/* Past due warning */}
      {status === "past_due" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">Payment failed</p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            Your last payment didn't go through. Update your payment method in the customer portal to
            restore uninterrupted access.
          </p>
        </div>
      )}

      {/* Current plan card */}
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">{tierLabel(tier)}</h2>
              <StatusBadge status={status} cancelAtPeriodEnd={cancelAtPeriodEnd} />
            </div>
            <p className="text-sm text-muted">
              {hasSubscription ? "Your active Founder subscription" : "You are on the free plan"}
            </p>
          </div>
          {planPrice !== null && hasSubscription && (
            <div className="text-right">
              <div className="text-2xl font-bold">${planPrice.toFixed(2)}</div>
              <div className="text-xs text-muted">per month</div>
            </div>
          )}
        </div>

        {/* Billing cycle stats */}
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-border bg-foreground/5 p-4">
            <div className="text-xs text-muted">Plan</div>
            <div className="mt-1 text-sm font-semibold">{tierLabel(tier)}</div>
          </div>
          <div className="rounded-md border border-border bg-foreground/5 p-4">
            <div className="text-xs text-muted">
              {cancelAtPeriodEnd ? "Access ends" : isTrialing ? "Trial ends" : "Next renewal"}
            </div>
            <div className="mt-1 text-sm font-semibold">{billingDateLabel}</div>
            {billingDateNote && (
              <div className="mt-0.5 text-xs text-muted">{billingDateNote}</div>
            )}
          </div>
          <div className="rounded-md border border-border bg-foreground/5 p-4">
            <div className="text-xs text-muted">Next charge</div>
            <div className="mt-1 text-sm font-semibold">
              {cancelAtPeriodEnd
                ? "No further charges"
                : isTrialing && planPrice !== null
                  ? `$${planPrice.toFixed(2)}`
                  : planPrice !== null && hasSubscription
                    ? `$${planPrice.toFixed(2)}`
                    : "—"}
            </div>
            {!cancelAtPeriodEnd && planPrice !== null && hasSubscription && (
              <div className="mt-0.5 text-xs text-muted">billed monthly</div>
            )}
          </div>
        </div>

        {hasSubscription && planFeatures.length > 0 && (
          <div className="mt-5 border-t border-border pt-4">
            <p className="mb-2.5 text-xs font-medium text-muted uppercase tracking-wide">What's included</p>
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {planFeatures.map((feat) => (
                <li key={feat} className="flex items-start gap-2 text-xs text-muted">
                  <CheckIcon />
                  {feat}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!hasSubscription && (
          <div className="mt-4 border-t border-border pt-4">
            <p className="mb-3 text-sm text-muted">
              Subscribe to unlock server management, Server Finder listings, and plugin integrations.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              View Plans
            </Link>
          </div>
        )}
      </div>

      {/* Usage card */}
      {hasSubscription && (
        <div className="rounded-lg border border-border bg-background p-6">
          <h2 className="mb-4 text-lg font-semibold">Usage</h2>
          <div className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">Organizations / Servers</span>
                <span className="text-sm text-muted">
                  {activeOrgs} / {serverLimit > 0 ? serverLimit : "—"}
                </span>
              </div>
              {serverLimit > 0 ? (
                <UsageBar used={activeOrgs} limit={serverLimit} />
              ) : (
                <p className="text-xs text-muted">No server slots on free plan.</p>
              )}
            </div>

            {activeOrgs >= serverLimit && serverLimit > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                  You've reached your server limit.
                </p>
                <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
                  Upgrade your plan to create more organizations.{" "}
                  <Link to="/dashboard/settings/billing" className="underline">
                    Manage plan
                  </Link>
                </p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-foreground/5 p-3">
                <div className="text-xs text-muted">Active organizations</div>
                <div className="mt-1 text-lg font-semibold">{activeOrgs}</div>
              </div>
              <div className="rounded-md border border-border bg-foreground/5 p-3">
                <div className="text-xs text-muted">Server slots available</div>
                <div className="mt-1 text-lg font-semibold">
                  {serverLimit > 0 ? Math.max(0, serverLimit - activeOrgs) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer portal card */}
      <div className="rounded-lg border border-border bg-background p-6">
        <h2 className="mb-1 text-lg font-semibold">Payment & Invoices</h2>
        <p className="mb-4 text-sm text-muted">
          View invoice history, download receipts, and update your saved payment method via the
          secure Polar customer portal.
        </p>

        {portalError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {portalError}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button onClick={handleOpenPortal} disabled={portalLoading || !hasSubscription}>
            {portalLoading ? "Opening portal..." : "Open Customer Portal →"}
          </Button>
          <Link
            to="/dashboard/settings/billing"
            className="inline-flex items-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-foreground/5"
          >
            Plan Settings
          </Link>
        </div>

        {!hasSubscription && (
          <p className="mt-3 text-xs text-muted">
            The customer portal is only available for active subscribers.
          </p>
        )}
      </div>

      {/* Upgrade CTA for active subscribers who can upgrade */}
      {hasSubscription && tier !== "pro" && (status === "active" || status === "trialing") && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold">Ready to grow?</p>
              <p className="mt-0.5 text-xs text-muted">
                Upgrade for more server slots and advanced features.
              </p>
            </div>
            <Link
              to="/dashboard/settings/billing"
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              View upgrade options
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
